
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ExternalLink, Calendar, Image as ImageIcon } from "lucide-react";
import { LazyImage } from "@/components/ui/lazy-image";
import { format } from "date-fns";
import { useState } from "react";

interface NewsCardProps {
  title: string;
  description: string | null;
  url: string;
  imageUrl: string | null;
  source: string;
  publishedAt: string;
}

export function NewsCard({ title, description, url, imageUrl, source, publishedAt }: NewsCardProps) {
  const [imageError, setImageError] = useState(false);

  // Clean any HTML from title and description (RSS feeds sometimes include HTML)
  const cleanHtml = (text: string): string => {
    return text.replace(/<\/?[^>]+(>|$)/g, "");
  };

  const cleanTitle = title ? cleanHtml(title) : "No Title Available";
  const cleanDescription = description ? cleanHtml(description) : "No description available.";

  // Format the date or use a fallback if invalid
  const formatDate = (dateString: string): string => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch (e) {
      console.error(`Invalid date format: ${dateString}`);
      return "Unknown date";
    }
  };

  // Generate content-specific fallback images based on title and description keywords
  const getFallbackImage = (): string => {
    const contentText = (cleanTitle + ' ' + cleanDescription).toLowerCase();
    
    // Check for content-specific keywords and assign specific placeholder images
    if (contentText.includes('cruise') || contentText.includes('ship') || contentText.includes('boat') || contentText.includes('ocean')) {
      return 'https://images.unsplash.com/photo-1501854140801-50d01698950b?auto=format&fit=crop&w=800&q=80';
    } else if (contentText.includes('hotel') || contentText.includes('resort') || contentText.includes('accommodation') || contentText.includes('stay')) {
      return 'https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=800&q=80';
    } else if (contentText.includes('mountain') || contentText.includes('hiking') || contentText.includes('trail') || contentText.includes('nature')) {
      return 'https://images.unsplash.com/photo-1472396961693-142e6e269027?auto=format&fit=crop&w=800&q=80';
    } else if (contentText.includes('beach') || contentText.includes('island') || contentText.includes('tropical') || contentText.includes('coast')) {
      return 'https://images.unsplash.com/photo-1473116763249-2faaef81ccda?auto=format&fit=crop&w=800&q=80';
    } else if (contentText.includes('city') || contentText.includes('urban') || contentText.includes('skyline')) {
      return 'https://images.unsplash.com/photo-1500916434205-0c77489c6cf7?auto=format&fit=crop&w=800&q=80';
    } else if (contentText.includes('food') || contentText.includes('restaurant') || contentText.includes('culinary') || contentText.includes('cuisine')) {
      return 'https://images.unsplash.com/photo-1476224203421-9ac39bcb3327?auto=format&fit=crop&w=800&q=80';
    } else if (contentText.includes('airport') || contentText.includes('flight') || contentText.includes('airplane') || contentText.includes('plane')) {
      return 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?auto=format&fit=crop&w=800&q=80';
    } else if (contentText.includes('train') || contentText.includes('railway') || contentText.includes('rail')) {
      return 'https://images.unsplash.com/photo-1474487548417-781cb71495f3?auto=format&fit=crop&w=800&q=80';
    } else if (contentText.includes('road trip') || contentText.includes('driving') || contentText.includes('car')) {
      return 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?auto=format&fit=crop&w=800&q=80';
    } else if (contentText.includes('desert') || contentText.includes('sand') || contentText.includes('dune')) {
      return 'https://images.unsplash.com/photo-1509316975850-ff9c5deb0cd9?auto=format&fit=crop&w=800&q=80';
    }
    
    // Source-specific fallbacks (if no content matches were found)
    if (source.toLowerCase().includes('travel and tour world') || source.toLowerCase().includes('tour')) {
      return 'https://images.unsplash.com/photo-1469041797191-50ace28483c3?auto=format&fit=crop&w=800&q=80';
    } else if (source.toLowerCase().includes('bbc')) {
      return 'https://images.unsplash.com/photo-1482938289607-e9573fc25ebb?auto=format&fit=crop&w=800&q=80';
    } else if (source.toLowerCase().includes('planet') || source.toLowerCase().includes('national')) {
      return 'https://images.unsplash.com/photo-1433086966358-54859d0ed716?auto=format&fit=crop&w=800&q=80';
    } else if (source.toLowerCase().includes('leisure')) {
      return 'https://images.unsplash.com/photo-1466721591366-2d5fba72006d?auto=format&fit=crop&w=800&q=80';
    }
    
    // Random selection from a set of high-quality travel images for everything else
    const fallbackImages = [
      'https://images.unsplash.com/photo-1465146344425-f00d5f5c8f07?auto=format&fit=crop&w=800&q=80',
      'https://images.unsplash.com/photo-1518495973542-4542c06a5843?auto=format&fit=crop&w=800&q=80',
      'https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&w=800&q=80',
      'https://images.unsplash.com/photo-1513836279014-a89f7a76ae86?auto=format&fit=crop&w=800&q=80',
      'https://images.unsplash.com/photo-1438565434616-3ef039228b15?auto=format&fit=crop&w=800&q=80'
    ];
    
    // Use the title string to deterministically select an image (so same article always gets same image)
    const titleHash = cleanTitle.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return fallbackImages[titleHash % fallbackImages.length];
  };

  // Handle image error by setting the error state
  const handleImageError = () => {
    setImageError(true);
  };

  // Determine the image source - either the actual image URL or a fallback
  const imageSource = !imageError && imageUrl ? imageUrl : getFallbackImage();

  return (
    <Card className="overflow-hidden flex flex-col h-full hover:shadow-md transition-shadow border border-border/80">
      <div className="aspect-video relative overflow-hidden bg-muted">
        <LazyImage 
          src={imageSource}
          alt={cleanTitle}
          className="w-full h-full object-cover transition-transform hover:scale-105 duration-300"
          onError={handleImageError}
        />
      </div>
      <CardHeader className="p-5 pb-2">
        <CardTitle className="text-lg sm:text-xl line-clamp-2 leading-tight">{cleanTitle}</CardTitle>
        <div className="flex items-center justify-between text-xs sm:text-sm text-muted-foreground mt-2">
          <span className="line-clamp-1 max-w-[120px] sm:max-w-[150px]">{source}</span>
          <div className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            <time dateTime={publishedAt}>
              {formatDate(publishedAt)}
            </time>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-5 pt-0 flex-grow">
        <p className="text-sm sm:text-base text-muted-foreground line-clamp-3 mt-2">
          {cleanDescription}
        </p>
      </CardContent>
      <CardFooter className="p-5 pt-0">
        <Button asChild className="w-full h-11" variant="outline">
          <a 
            href={url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center justify-center"
          >
            Read Full Article <ExternalLink className="ml-2 h-5 w-5" />
          </a>
        </Button>
      </CardFooter>
    </Card>
  );
}
